// ImageLightbox.tsx
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { ImageThumbnailStrip } from './ImageThumbnailStrip';

interface ImageLightboxProps {
  images: Array<{ url: string; caption: string }>;
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}

export default function ImageLightbox({
  images,
  currentIndex,
  isOpen,
  onClose,
  onPrevious,
  onNext,
}: ImageLightboxProps) {
  const [isZoomed, setIsZoomed] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  // Preload images
  useEffect(() => {
    const preloadImages = () => {
      const imagesToLoad = [
        // Current image
        images[currentIndex].url,
        // Next image
        ...(currentIndex < images.length - 1
          ? [images[currentIndex + 1].url]
          : []),
        // Previous image
        ...(currentIndex > 0 ? [images[currentIndex - 1].url] : []),
      ];

      imagesToLoad.forEach(url => {
        if (!loadedImages.has(url)) {
          const img = new Image();
          img.src = url;
          img.onload = () => {
            setLoadedImages(prev => new Set([...prev, url]));
          };
        }
      });
    };

    preloadImages();
  }, [currentIndex, images, loadedImages]);

  // Handle keyboard navigation
  const handleThumbnailSelect = (index: number) => {
    setIsZoomed(false);
    setDragPosition({ x: 0, y: 0 });

    if (index < currentIndex) {
      onPrevious();
    }
    if (index > currentIndex) {
      onNext();
    }
  };

  // Handle swipe gestures
  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    if (isZoomed) return;

    const SWIPE_THRESHOLD = 50;
    if (info.offset.x > SWIPE_THRESHOLD) {
      onPrevious();
    } else if (info.offset.x < -SWIPE_THRESHOLD) {
      onNext();
    }
  };

  const handleZoomClick = () => {
    setIsZoomed(prev => !prev);
    setDragPosition({ x: 0, y: 0 });
  };

  const handleImageDrag = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    if (!isZoomed) return;

    setDragPosition(prev => ({
      x: prev.x + info.delta.x,
      y: prev.y + info.delta.y,
    }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center"
          onClick={onClose}
        >
          {/* Controls */}
          <div className="absolute top-4 right-4 flex items-center gap-2 z-50">
            <button
              onClick={e => {
                e.stopPropagation();
                handleZoomClick();
              }}
              className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
            >
              {isZoomed ? <ZoomOut size={24} /> : <ZoomIn size={24} />}
            </button>
            <button
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
              className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Navigation buttons */}
          {!isZoomed && (
            <>
              <button
                onClick={e => {
                  e.stopPropagation();
                  onPrevious();
                }}
                className="absolute left-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors disabled:opacity-50"
                disabled={currentIndex === 0}
              >
                <ChevronLeft size={32} />
              </button>

              <button
                onClick={e => {
                  e.stopPropagation();
                  onNext();
                }}
                className="absolute right-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors disabled:opacity-50"
                disabled={currentIndex === images.length - 1}
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}

          {/* Image container */}
          <motion.div
            ref={containerRef}
            className="w-full max-w-5xl px-12 flex-1 flex items-center justify-center"
            onClick={e => e.stopPropagation()}
            drag={isZoomed}
            dragConstraints={containerRef}
            onDragEnd={handleImageDrag}
            animate={{
              x: dragPosition.x,
              y: dragPosition.y,
              scale: isZoomed ? 2 : 1,
              cursor: isZoomed ? 'grab' : 'auto',
            }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="relative w-full h-full flex items-center justify-center"
              drag={!isZoomed}
              dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
              dragElastic={0.1}
              onDragEnd={handleDragEnd}
            >
              <img
                src={images[currentIndex].url}
                alt={images[currentIndex].caption}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                style={{ userSelect: 'none' }}
                onDoubleClick={handleZoomClick}
              />
            </motion.div>
          </motion.div>

          {/* Caption and counter */}
          <div className="w-full text-center pb-4">
            {images[currentIndex].caption && (
              <p className="text-white text-center mt-4">
                {images[currentIndex].caption}
              </p>
            )}
            <p className="text-white/70 text-center mt-2">
              {currentIndex + 1} / {images.length}
            </p>
          </div>

          {/* Thumbnail strip */}
          <div className="w-full max-w-5xl px-4 pb-4">
            <ImageThumbnailStrip
              images={images}
              currentIndex={currentIndex}
              onSelect={handleThumbnailSelect}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
